import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

export interface Notification {
  id: string;
  type: "clone" | "general";
  title: string;
  message: string;
  status: "in-progress" | "completed" | "failed";
  timestamp: Date;
  taskId?: string;
  experimentName?: string;
  progress?: number;
}

@Injectable({
  providedIn: "root",
})
export class NotificationService {
  private notifications: Notification[] = [];
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  public notifications$: Observable<Notification[]> =
    this.notificationsSubject.asObservable();

  private hasInProgressSubject = new BehaviorSubject<boolean>(false);
  public hasInProgress$: Observable<boolean> =
    this.hasInProgressSubject.asObservable();

  constructor() {
    // Load notifications from localStorage on init
    this.loadNotifications();
  }

  addNotification(notification: Notification): void {
    this.notifications.unshift(notification);
    this.saveNotifications();
    this.updateInProgressStatus();
  }

  updateNotification(id: string, updates: Partial<Notification>): void {
    const index = this.notifications.findIndex((n) => n.id === id);
    if (index !== -1) {
      this.notifications[index] = { ...this.notifications[index], ...updates };
      this.saveNotifications();
      this.updateInProgressStatus();
    }
  }

  startCloneNotification(taskId: string, experimentName: string): string {
    const notificationId = `clone_${taskId}_${Date.now()}`;
    const notification: Notification = {
      id: notificationId,
      type: "clone",
      title: "Cloning Experiment",
      message: `Cloning "${experimentName}" in progress...`,
      status: "in-progress",
      timestamp: new Date(),
      taskId: taskId,
      experimentName: experimentName,
      progress: 0,
    };
    this.addNotification(notification);
    return notificationId;
  }

  updateCloneProgress(notificationId: string, progress: number): void {
    this.updateNotification(notificationId, { progress });
  }

  completeCloneNotification(notificationId: string): void {
    const notification = this.notifications.find(
      (n) => n.id === notificationId,
    );
    if (notification) {
      this.updateNotification(notificationId, {
        status: "completed",
        message: `"${notification.experimentName}" cloned successfully!`,
        progress: 100,
      });
    }
  }

  failCloneNotification(notificationId: string, error?: string): void {
    const notification = this.notifications.find(
      (n) => n.id === notificationId,
    );
    if (notification) {
      this.updateNotification(notificationId, {
        status: "failed",
        message: error || `Failed to clone "${notification.experimentName}"`,
      });
    }
  }

  removeNotification(id: string): void {
    this.notifications = this.notifications.filter((n) => n.id !== id);
    this.saveNotifications();
    this.updateInProgressStatus();
  }

  clearAllNotifications(): void {
    this.notifications = [];
    this.saveNotifications();
    this.updateInProgressStatus();
  }

  clearCompletedNotifications(): void {
    this.notifications = this.notifications.filter(
      (n) => n.status === "in-progress",
    );
    this.saveNotifications();
    this.updateInProgressStatus();
  }

  getNotifications(): Notification[] {
    return this.notifications;
  }

  hasInProgressNotifications(): boolean {
    return this.notifications.some((n) => n.status === "in-progress");
  }

  private updateInProgressStatus(): void {
    const hasInProgress = this.hasInProgressNotifications();
    this.hasInProgressSubject.next(hasInProgress);
  }

  private saveNotifications(): void {
    // Keep only last 50 notifications to avoid localStorage bloat
    const notificationsToSave = this.notifications.slice(0, 50);
    localStorage.setItem(
      "app_notifications",
      JSON.stringify(notificationsToSave),
    );
    this.notificationsSubject.next([...this.notifications]);
  }

  private loadNotifications(): void {
    const saved = localStorage.getItem("app_notifications");
    if (saved) {
      try {
        this.notifications = JSON.parse(saved).map((n: any) => ({
          ...n,
          timestamp: new Date(n.timestamp),
        }));
        // Clear any old in-progress notifications (they might be stale)
        this.notifications = this.notifications.filter(
          (n) => n.status !== "in-progress",
        );
        this.saveNotifications();
        this.updateInProgressStatus();
      } catch (e) {
        console.error("Error loading notifications:", e);
        this.notifications = [];
      }
    }
  }
}
